import { useLenis } from 'lenis/react'
import { useEffect, useRef } from 'react'
import { Icon } from '@/components/ui'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { gsap } from '@/lib/gsap'
import { damp } from '@/lib/math'
import { pointer } from '@/lib/pointer'
import { useAppStore } from '@/lib/store'
import styles from './Cursor.module.css'
import { resolveCursor, type CursorTarget } from './resolveCursor'

/** Ring follow responsiveness (see `damp`): fluid, but never so slow it feels broken. */
const RING_LAMBDA = 11
/** How quickly the velocity stretch settles. */
const STRETCH_LAMBDA = 9
/** Speed (px/s) that gives the full stretch, and the stretch cap (0.2 = 20% longer). */
const STRETCH_SPEED = 2800
const STRETCH_MAX = 0.2

/**
 * The custom cursor: a slate dot on the pointer and a soft ring trailing it with damped,
 * velocity-stretched motion. It morphs by `data-cursor` (one delegated listener on
 * `document`): `link` grows an iris ring, `view` / `drag` become a glass pill with a label,
 * `text` becomes a caret, `hide` steps aside. Colours follow the section's `data-theme`.
 *
 * Fine pointers only, never under reduced motion, and only once the preloader has started
 * leaving. The native cursor is hidden only while this one is actually visible.
 */
export function Cursor() {
  const finePointer = useMediaQuery(MEDIA.hover)
  const reducedMotion = useReducedMotion()
  const introStarted = useAppStore((state) => state.phase !== 'boot')
  if (!finePointer || reducedMotion || !introStarted) return null
  return <CursorLayer />
}

function CursorLayer() {
  const rootRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<HTMLDivElement>(null)
  const shapeRef = useRef<HTMLDivElement>(null)
  const dotRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const applyRef = useRef<((target: CursorTarget) => void) | null>(null)
  const settleTimer = useRef(0)

  useEffect(() => {
    const root = rootRef.current
    const ring = ringRef.current
    const shape = shapeRef.current
    const dot = dotRef.current
    const label = labelRef.current
    if (!root || !ring || !shape || !dot || !label) return

    const html = document.documentElement
    let visible = false
    let state = ''
    let theme = ''
    let currentLabel = ''
    let ringX = pointer.x
    let ringY = pointer.y
    let dotX = Number.NaN
    let dotY = Number.NaN
    let stretch = 0
    let angle = 0
    let resting = false

    const setVisible = (next: boolean) => {
      if (visible === next) return
      visible = next
      root.dataset.visible = String(next)
      if (next) html.setAttribute('data-vk-cursor', '')
      else html.removeAttribute('data-vk-cursor')
    }

    const apply = (target: CursorTarget) => {
      if (target.state !== state) {
        state = target.state
        root.dataset.state = state
      }
      if (target.theme !== theme) {
        theme = target.theme
        root.dataset.theme = theme
      }
      if (target.label !== currentLabel) {
        currentLabel = target.label
        label.textContent = currentLabel
      }
    }
    applyRef.current = apply

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') {
        setVisible(false)
        return
      }
      resting = false
      if (!visible) {
        // Arrive where the pointer is, instead of flying in from where it left.
        ringX = event.clientX
        ringY = event.clientY
        apply(resolveCursor(event.target as Element))
        setVisible(true)
      }
    }
    const onPointerOver = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') apply(resolveCursor(event.target as Element))
    }
    const onPointerOut = (event: PointerEvent) => {
      if (!event.relatedTarget) setVisible(false)
    }
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') root.dataset.pressed = 'true'
    }
    const onPointerUp = () => {
      root.dataset.pressed = 'false'
    }
    const onWindowBlur = () => setVisible(false)

    const tick = (_time: number, deltaMs: number) => {
      if (!visible || resting) return
      const dt = Math.min(Math.max(deltaMs, 1), 64) / 1000
      const targetX = pointer.x
      const targetY = pointer.y

      if (targetX !== dotX || targetY !== dotY) {
        dotX = targetX
        dotY = targetY
        dot.style.transform = `translate3d(${dotX}px, ${dotY}px, 0)`
      }

      const previousX = ringX
      const previousY = ringY
      ringX = damp(ringX, targetX, RING_LAMBDA, dt)
      ringY = damp(ringY, targetY, RING_LAMBDA, dt)
      const vx = (ringX - previousX) / dt
      const vy = (ringY - previousY) / dt
      const speed = Math.hypot(vx, vy)
      // Only steer while really moving, so the angle never jitters at rest.
      if (speed > 60) angle = Math.atan2(vy, vx)
      const stretchTarget =
        state === 'default' || state === 'link' ? Math.min(speed / STRETCH_SPEED, STRETCH_MAX) : 0
      stretch = damp(stretch, stretchTarget, STRETCH_LAMBDA, dt)

      const near = Math.abs(ringX - targetX) < 0.04 && Math.abs(ringY - targetY) < 0.04
      if (near && stretch < 0.0004) {
        // Settle exactly and stop writing until the pointer moves again.
        ringX = targetX
        ringY = targetY
        stretch = 0
        resting = true
      }
      ring.style.transform = `translate3d(${ringX.toFixed(2)}px, ${ringY.toFixed(2)}px, 0)`
      shape.style.transform = `rotate(${angle.toFixed(4)}rad) scale(${(1 + stretch).toFixed(4)}, ${(1 - stretch * 0.5).toFixed(4)})`
    }

    gsap.ticker.add(tick)
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    document.addEventListener('pointerover', onPointerOver, { passive: true })
    document.addEventListener('pointerout', onPointerOut, { passive: true })
    window.addEventListener('pointerdown', onPointerDown, { passive: true })
    window.addEventListener('pointerup', onPointerUp, { passive: true })
    window.addEventListener('blur', onWindowBlur)

    return () => {
      gsap.ticker.remove(tick)
      window.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerover', onPointerOver)
      document.removeEventListener('pointerout', onPointerOut)
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('blur', onWindowBlur)
      html.removeAttribute('data-vk-cursor')
      applyRef.current = null
    }
  }, [])

  // Content moves under a resting pointer while scrolling; re-read the element under it
  // once the scroll settles (a timer, not a layout read inside the scroll handler).
  useLenis(() => {
    window.clearTimeout(settleTimer.current)
    settleTimer.current = window.setTimeout(() => {
      applyRef.current?.(resolveCursor(document.elementFromPoint(pointer.x, pointer.y)))
    }, 140)
  })
  useEffect(() => () => window.clearTimeout(settleTimer.current), [])

  return (
    <div
      ref={rootRef}
      className={styles.cursor}
      data-visible="false"
      data-state="default"
      data-theme="light"
      aria-hidden="true"
    >
      <div ref={ringRef} className={styles.ringPos}>
        <div ref={shapeRef} className={styles.ringShape}>
          <span className={styles.ring} />
        </div>
        <span className={styles.pill}>
          <span className={styles.pillIcon} data-for="drag">
            <svg viewBox="0 0 12 12" width="10" height="10">
              <path d="M7.5 2.5 4 6l3.5 3.5" />
            </svg>
          </span>
          <span ref={labelRef} className={styles.pillLabel} />
          <span className={styles.pillIcon} data-for="drag">
            <svg viewBox="0 0 12 12" width="10" height="10">
              <path d="M4.5 2.5 8 6 4.5 9.5" />
            </svg>
          </span>
          <Icon name="arrow-up-right" className={styles.pillIcon} data-for="view" />
        </span>
      </div>
      <div ref={dotRef} className={styles.dotPos}>
        <span className={styles.dot} />
      </div>
    </div>
  )
}
